import {
  PRICE_ASC,
  PRICE_DESC,
  DISCOUNT,
  FEATURED,
  SIZE,
  TYPE,
  COLOUR,
  COLOUR_TAGS,
} from "../constants";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useCurrencyState } from "@hooks/useCurrency";

export const useFilterSort = ({ products, filteredValues, sortBy }) => {
  const router = useRouter();
  const { currency } = useCurrencyState();
  const [productList, setProductList] = useState([]);
  const isFrench = router.locale === "fr";
  const matchesColourSpelling = (string) => string.toLowerCase().match(/([Cc](o|ou)l(o|ou|eu)r)/g);

  useEffect(() => {
    if (products.length > 0) {
      const listWithSortFields = addSortFields(products);
      const filteredList = filter(listWithSortFields);
      const sortedList = sort(filteredList);
      setProductList(sortedList);
    } else {
      setProductList([]);
    }
  }, [products, filteredValues, sortBy]);

  const addSortFields = (list) => {
    const listWithSortFields = list.map((product, index) => {
      const price = getPrice(product, currency);
      const discount = getDiscountPercent(product, currency);

      return {
        ...product,
        sortBy: {
          price: price,
          discount: discount,
          featured: index,
        },
      };
    });

    return listWithSortFields;
  };

  const filters = Object.keys(filteredValues);
  const getColourOption = (product) =>
    product.options.filter((option) => matchesColourSpelling(option.name))[0];
  const getSizeOption = (variant) =>
    variant.selectedOptions.filter((option) => option.name === "Size")[0];

  const filter = (list) => {
    const filteredProducts = list.filter((product) => {
      const { variants } = product;
      const colourOption = getColourOption(product);
      const shopifyProducts = product.sanity?.shopifyProducts;
      const productsWithTags =
        shopifyProducts && shopifyProducts.filter((product) => product.colour?.tags);

      const found = filters.every((value) => {
        if (filteredValues[value].length === 0) {
          return true;
        }

        if (value === COLOUR) {
          return (
            colourOption &&
            colourOption.values.some((val) => filteredValues[COLOUR].includes(val.toLowerCase()))
          );
        }

        if (value === SIZE) {
          return variants.some((variant) => {
            const size = getSizeOption(variant);
            if (!size) return false;

            if (filteredValues[SIZE][0] === size.value.toLowerCase()) {
              return variant.availableForSale;
            }
          });
        }

        if (value === TYPE) {
          const typeEn = product.tags.find((tag) => tag.includes("type:"));
          const typeFr = product.tags.find((tag) => tag.includes("type-fr:"));
          const type = (isFrench && typeFr) ? typeFr : typeEn;

          if (type) return filteredValues[TYPE].includes(type.toLowerCase());
        }

        if (value === COLOUR_TAGS) {
          if (product.sanity) {
            // get the color tag and handle for each product with color tags
            const productData = productsWithTags.map((product) => {
              const tags = (isFrench && product.colour?.tags_fr)
                ? product.colour?.tags_fr
                : product.colour?.tags;

              return tags.map((tag) => ({
                tag: tag.value.toLowerCase(),
                handle: product.handle,
              }));
            });

            const flattenedData = [].concat.apply([], productData);

            // return the handle of the product that matches
            // both the color tag AND product handle
            const values = flattenedData.map((data) => {
              const tagMatches = filteredValues[COLOUR_TAGS].some((value) => data.tag === value);
              const handleMatches = product.handle === data.handle;

              if (tagMatches && handleMatches) return product.handle;
              return false;
            });

            // if the handle matches a product handle, show that product
            return values.some((value) => product.handle === value);
          }

          return false;
        }
      });

      if (found) {
        return product;
      }
    });

    return filteredProducts;
  };

  const getPrice = (product, currency) => {
    const { variants } = product;

    const price = variants.map((variant) => {
      const price = variant.presentmentPrices
        .filter((item) => item.price.currencyCode === currency)
        .map((item) => item.price.amount);

      return price[0];
    });

    return parseFloat(price[0]);
  };

  const getDiscountPercent = (product, currency) => {
    const { variants } = product;

    const prices = variants.map((variant) => {
      const items = variant.presentmentPrices
      .filter((item) => item.price.currencyCode === currency)
      .map((item) => ({
        price: item.price.amount,
        compareAtPrice: item.compareAtPrice?.amount ? item.compareAtPrice.amount : 0,
      }));

      return items[0];
    });

    const price = prices[0]?.price || 0;
    const compareAtPrice = prices[0]?.compareAtPrice || 0;
    const discount = Math.floor(100 - (parseFloat(price) / parseFloat(compareAtPrice)) * 100);

    return discount > 0 ? discount : 0;
  };

  const sortAsc = (list, value) => list.sort((a, b) => a.sortBy[value] - b.sortBy[value]);
  const sortDesc = (list, value) => list.sort((a, b) => b.sortBy[value] - a.sortBy[value]);

  const sort = (list) => {
    if (sortBy === PRICE_ASC) return sortAsc(list, "price");
    if (sortBy === PRICE_DESC) return sortDesc(list, "price");
    if (sortBy === DISCOUNT) return sortDesc(list, "discount");
    if (sortBy === FEATURED) return sortAsc(list, "featured");
    if (sortBy === "") return sortAsc(list, "featured");
  };

  return { productList };
};
